import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface AuthState {
  isLoggedIn: boolean;
  username: string | null;
  id: string | null;
  email: string | null;
}

const initialAuthState = {
  isLoggedIn: false,
  username: null,
  id: null,
  email: null,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _authState = new BehaviorSubject<AuthState>(
    initialAuthState
  );

  hideMenus = true;

  private _groups: string[] = [];

  public get groups(): string[] {
    return this._groups;
  }

  public set groups(value: string[]) {
    this._groups = value;
  }

  /** AuthState as an Observable */
  readonly auth$ = this._authState.asObservable();

  /** Observe the isLoggedIn slice of the auth state */
  readonly isLoggedIn$ = this.auth$.pipe(map((state) => state.isLoggedIn));

  constructor(private router: Router, private zone: NgZone) {
    // Get the user on creation of this service
    getCurrentUser().then(
      (user: any) => this.setUserFromCurrentUser(user),
      (_err) => this._authState.next(initialAuthState)
    );

    // Use Hub channel 'auth' to get notified on changes
    Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn' || event === 'tokenRefresh') {
        this.refreshUserData();
        if(event === 'signedIn')
          window.location.reload();
      } else if (event === 'signedOut') {
        this._authState.next(initialAuthState);
      }
    });
  }

  isLoggedIn = () => {
    return !!this.isLoggedIn$;
  };

  public signOut(): void {
    this._authState.next(initialAuthState);
    this.hideMenus = true;
    this.groups = [];

    // Perform AWS Cognito sign out
    signOut()
      .then(() => {
        setTimeout(() => {
          this.router.navigate(['/auth'])

          // Force reload if redirect doesn't happen
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }, 100);
      })
      .catch((err) => {
        console.log('signOut error: ', err);
        window.location.replace('/#/auth');
      });
  }

  private async refreshUserData() {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      this.hideMenus = false;
      this.groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];

      const { username, userId } = user;
      const email = session.tokens?.idToken?.payload['email'] as string;

      this._authState.next({ isLoggedIn: true, id: userId, username, email });
    } catch (err) {
      this.hideMenus = true;
      this._authState.next(initialAuthState);
    }
  }

  private async setUserFromCurrentUser(user: any) {
    if (!user) {
      this.hideMenus = true;
      return;
    }

    try {
      const session = await fetchAuthSession();
      this.hideMenus = false;
      this.groups = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];

      const { username, userId } = user;
      const email = session.tokens?.idToken?.payload['email'] as string;

      this._authState.next({ isLoggedIn: true, id: userId, username, email });
    } catch (err) {
      this.hideMenus = true;
      this._authState.next(initialAuthState);
    }
  }

  public hasGroup(groupName: string): boolean {
    return this.groups?.includes(groupName);
  }
}
